<template>
  <div class="user-login" @keydown.enter="login" tabindex="0">
    <div class="form_area">
      <AppleLogin :isLogin="true"></AppleLogin>
      <GoogleLoginServer
        :isLogin="true"
        @success="googleLogin"
        @click="googleLogin"
        class="google-login"
      ></GoogleLoginServer>

      <div class="login_title">
        or
      </div>
      <div class="row">
        <v-text-field
          :label="t('メールアドレス')"
          prepend-inner-icon="mdi-email"
          outlined
          v-model="mail"
          hide-details="auto"
        ></v-text-field>
      </div>
      <div class="row">
        <v-text-field
          :label="t('パスワード')"
          prepend-inner-icon="mdi-lock"
          outlined
          :type="showPassword ? 'text' : 'password'"
          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          @click:append="togglePassword"
          v-model="password"
          hide-details="auto"
        ></v-text-field>
      </div>
      <ErrorMessage
        v-if="errorMessage"
        class="error-message"
        :message="errorMessage"
      />
      <div class="check-box-container">
        <CheckBox
          class="check-box"
          :isChecked="isChecked"
          :isBgWhite="true"
          @handleCheckBoxClick="toggleCheck"
        />
        <span class="text">{{ t('ログイン状態を保持する') }}</span>
      </div>
      <ToggleBtn
        class="square-button login"
        :text="t('ログイン')"
        :isAction="canLogin"
        :isDisabled="!canLogin"
        @handleSquareBtnClick="login"
      />
      <div class="forget-password">
        <span class="text" @click.stop="forgetPassword">{{ t('パスワードをお忘れですか？') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import CheckBox from '@/components/atoms/CheckBox.vue';
import ErrorMessage from '@/components/atoms/ErrorMessage.vue';
import ToggleBtn from '@/components/atoms/ToggleBtn.vue';
import GoogleLoginServer from '@/components/atoms/GoogleLoginServer.vue';
import AppleLogin from '@/components/atoms/AppleLogin.vue';
import t from '@/plugins/i18n';

export default {
  name: 'UserLogin',
  mixins: [t],

  components: {
    CheckBox,
    ErrorMessage,
    ToggleBtn,
    GoogleLoginServer,
    AppleLogin,
  },

  data: () => ({
    mail: null,
    password: null,
    isChecked: false,
    isShowPassword: false,
    errorMessage: '',
    showPassword: false,
  }),

  computed: {
    ...mapGetters({
      loginUserResult: 'user/loginUserResult',
      googleLoginResult: 'user/googleLoginResult',
    }),
    canLogin() {
      if (this.mail && this.password) {
        return true;
      }
      return false;
    },
  },

  created() {
    const { apple, login_error: loginError } = this.$route.query;
    if (apple) {
      if (apple === '1') {
        // Appleとの連携に失敗しました。もう一度お試しください。
        this.errorMessage = this.t('Appleとの連携に失敗しました。もう一度お試しください。');
      }
      if (apple === '2') {
        // 以下のURLから当サイト情報との紐付けを削除し再度お試し下さい。
        // https://appleid.apple.com/#!&page=signin
        // window.alert('Please remove the link with Apple from the following URL and try again.\nhttps://appleid.apple.com/#!&page=signin');
        this.errorMessage = `${this.t('以下のURLから当サイト情報との紐付けを削除し再度お試し下さい。')}\nhttps://appleid.apple.com/#!&page=signin`;
      }
      if (apple === '3') {
        // このアカウントは既に登録されています。ログインしてお楽しみください。
        // window.alert('This account is already registered.\nPlease log in and enjoy.');
        this.errorMessage = this.t('このアカウントは既に登録されています。ログインしてお楽しみください。');
      }
    }
    if (loginError) {
      this.errorMessage = this.t('メールアドレスまたはパスワードが一致しません。');
    }
  },

  watch: {
    loginUserResult(newVal) {
      if (newVal) {
        if (newVal.state === 200) {
          this.$store.dispatch('user/clearLoginUserResult');
          this.$store.dispatch('user/setShowLoginPage', false);
          this.$store.dispatch('user/setCurrentUser');
          this.$store.dispatch('user/setCurrentUserDetail');
          const { packId } = this.$route.query;
          if (packId) {
            this.$router.push({ path: '/pack-detail', query: { packId } });
            return;
          }
          // purchase-cardページの場合は現在のページに留まる
          if (this.$route.path === '/purchase-card') {
            return;
          }
          this.$router.push('/');
        } else {
          const { mail } = newVal.errors;
          this.errorMessage = mail ? mail[0] : null;
        }
      }
    },
    googleLoginResult(newVal) {
      if (newVal) {
        if (newVal.state === 200) {
          this.$store.dispatch('user/clearLoginUserResult');
          const { packId } = this.$route.query;
          if (packId) {
            this.$router.push({ path: '/pack-detail', query: { packId } });
            return;
          }
          // purchase-cardページの場合は現在のページに留まる
          if (this.$route.path === '/purchase-card') {
            this.$store.dispatch('user/setShowLoginPage', false);
            this.$store.dispatch('user/setCurrentUser');
            this.$store.dispatch('user/setCurrentUserDetail');
            return;
          }
          this.$router.push('/');
        } else {
          window.alert(newVal.errors.mail[0]);
        }
      }
    },
  },

  methods: {
    inputMail(mail) {
      this.mail = mail;
    },
    inputPassword(password) {
      this.password = password;
    },
    toggleShowPassword() {
      this.isShowPassword = !this.isShowPassword;
    },
    toggleCheck() {
      this.isChecked = !this.isChecked;
    },
    login(e) {
      // 日本語入力中のEnterのkeyCode(229)の場合は何もしない。
      if (e && e.keyCode === 229) {
        return;
      }

      if (!this.canLogin) {
        return;
      }

      const postParams = {
        mail: this.mail,
        pass: this.password,
      };
      if (this.isChecked) {
        postParams.save_cookie = '1';
      }
      this.$store.dispatch('user/loginUser', { postParams });
    },
    forgetPassword() {
      this.$router.push('/password-reissue');
      this.$store.dispatch('user/setShowLoginPage', false);
    },
    goToUserRegister() {
      this.$router.push('/user-register');
    },
    googleLogin() {
      window.location.href = '/api/user/google/login';
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
  },

};
</script>

<style lang="scss" scoped>
.user-login{
  width: 100%;
  max-width: 800px;
  min-height: 100%;
  padding: 20px 20px;
  margin: 0px auto;
  text-align: center;
  outline: none;
  position: relative;
  z-index: 1;
  .google-login {
    margin-top: 20px;
  }
  .form_area {
    max-width: 680px;
    margin: auto;
    h2 {
      text-align: center;
      font-weight: bold;
      font-size: 24px;
    }
    .row {
      margin: 20px 0;
      @media screen and  (max-width: $tabletWidth) {
        justify-content: center;
      }
      .v-text-field {
        border-radius: 10px;
      }
    }
  }
  .label-password {
    margin-top: 20px;
  }
  .text-box{
    width: 315px;
    margin: 0px auto;
    &.mail{
      width: 100%;
    }
    &.password{
      width: 100%;
      .icon.eye{
        position: relative;
        margin: 0px 5px 0px 10px;
        .fa-eye, .fa-slash{
          font-size: 18px;
          line-height: 1.5;
          color: $brown;
        }
        .fa-slash{
          position: absolute;
          top: calc(50% - 8px);
          left: calc(50% - 11px);
        }
      }
    }
  }
  .error-message{
    width: 315px;
    margin: 0px auto;
  }
  .check-box-container{
    display: flex;
    // justify-content: center;
    align-items: center;
    margin: 20px auto 20px;
    .check-box{
      border: 1px solid #d7d8dc;
      border-radius: 4px;
      width: 25px;
      height: 25px;
    }
    .text{
      margin-left: 10px;
      font-family: $notoSans;
      font-size: 14px;
      line-height: 1.8;
    }
  }
  .square-button{
    // width: 280px;
    height: 50px;
    margin: 10px auto 0px;
    border-radius: 100px;
    font-weight: bold;
  }
  .forget-password{
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin: 10px auto 0px;
    color: #2fa7d7;
    .text{
      font-family: $notoSans;
      font-size: 14px;
      cursor: pointer;
      transition: .4s;
      &:hover {
        opacity: .4;
      }
    }
    .fa-angle-right{
      margin-left: 10px;
      font-size: 21px;
      line-height: 27px;
      color: $deepGray;
    }
  }
  .description{
    text-align: center;
    height: 66px;
    margin-top: 10px;
    .alert {
      position: relative;
      font-size: 20px;
      font-weight: bold;
      color: #f86560;
      padding-left: 40px;
      &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        display: block;
        background-image: url('/img/icons/alert_icon.png');
        background-position: center;
        background-size: 100%;
        background-repeat: no-repeat;
        width: 30px;
        height: 30px;
      }
    }
    .text{
      font-family: $notoSans;
      font-size: 13px;
      line-height: 23.4px;
    }
  }
  .sub_box {
    margin-top: 30px;
    margin-bottom: 50px;
  }
}
.radio_area {
  label {
    display: flex;
    align-items: center;
    .radio {
      width: 16px;
      height: 16px;
      margin-right: 10px;
    }
    span {
      color: #fff;
    }
  }
}
.login_title {
  margin-top: 20px;
}
@media screen and  (max-width: $tabletWidth) {
  .user-login{
    .form_area {
      padding: 0 20px;
    }
    .sub_box {
      margin-bottom: 20px;
    }
    .description{
      .alert {
        font-size: 18px;
        font-weight: bold;
        color: #f86560;
        padding-left: 30px;
        &:before {
          width: 24px;
          height: 24px;
        }
      }
    }
  }
}
</style>
